import { Flex, Stack, Text } from "@mantine/core";
import { DatePickerInput } from "@mantine/dates";

import { DateFormat, FormInputItem, LooseObject } from "~/utils/Types";
import dayjs, { Dayjs } from "~/utils/dayjs";

import { earliest, getDateLimits, latest } from "./dateLimits";

export type DatePickerProps = {
  label?: string;
  name?: string;
  item: FormInputItem;
  textFieldProps: LooseObject;
  values: LooseObject;
  setFieldValue: (field: string, value: unknown) => void;
  minDate?: Date;
  maxDate?: Date;
  inputProps?: Record<string, unknown>;
  format?: string;
  showFloatingLabel?: boolean;
};

const DateRangePicker = ({
  item,
  values,
  setFieldValue,
  textFieldProps,
  format,
}: DatePickerProps) => {
  const limits = getDateLimits(item);
  const from = values[`${item.name}From`]
    ? dayjs(values[`${item.name}From`]).toDate()
    : undefined;
  const to = values[`${item.name}To`]
    ? dayjs(values[`${item.name}To`]).toDate()
    : undefined;

  return (
    <Stack w="100%" style={textFieldProps.sx}>
      {item.label && (
        <Text size="sm" fw={500} mb="xs">
          {item.label}
        </Text>
      )}
      <Flex gap="md">
        <DatePickerInput
          label="From"
          value={
            values[`${item.name}From`]
              ? dayjs(values[`${item.name}From`]).toDate()
              : (item.defaultValue as { from?: Dayjs; to?: Dayjs } | undefined)
                    ?.from
                ? dayjs((item.defaultValue as { from: Dayjs }).from).toDate()
                : null
          }
          onChange={(date) => {
            const dayjsDate = date ? dayjs(date) : null;
            setFieldValue(`${item.name}From`, dayjsDate);
            item.exposeValue?.(dayjsDate);
          }}
          minDate={limits.minDate}
          maxDate={earliest(to, limits.maxDate)}
          valueFormat={format || DateFormat.SHORT}
          clearable
          style={{ width: "100%" }}
        />
        <DatePickerInput
          label="To"
          value={
            values[`${item.name}To`]
              ? dayjs(values[`${item.name}To`]).toDate()
              : (item.defaultValue as { from?: Dayjs; to?: Dayjs } | undefined)
                    ?.to
                ? dayjs((item.defaultValue as { to: Dayjs }).to).toDate()
                : null
          }
          onChange={(date) => {
            const dayjsDate = date ? dayjs(date) : null;
            setFieldValue(`${item.name}To`, dayjsDate);
            item.exposeValue?.(dayjsDate);
          }}
          minDate={latest(from, limits.minDate)}
          maxDate={limits.maxDate}
          valueFormat={format || DateFormat.SHORT}
          clearable
          style={{ width: "100%" }}
        />
      </Flex>
    </Stack>
  );
};

export default DateRangePicker;
